import React, { useState, useEffect, useMemo, useCallback, useRef } from "react";
import { createPortal } from "react-dom";
import { Modal, Space, message, Input, Button, Popconfirm, Select, DatePicker } from "antd";
import { PlusOutlined, DeleteOutlined, EditOutlined, SearchOutlined, DownloadOutlined, FileExcelOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";

import { Method_POST_CONTEXTO } from "../../../../service/api";
import { useLivroRazaoDownload } from "./DownloadLivroRazao/indexx";
import { ModalEditarLoteLivroRazao } from "./ModalEditarLoteLivroRazao";
import { exportLivroRazaoExcel, exportLancamentosExcel } from "./DownloadLivroRazao/util";

// Sub-components and Utils
import { buildSaldoMap, agruparLancamentosPorConta } from "./utils";
import RazaoHeader from "./components/RazaoHeader";
import RazaoTable from "./components/RazaoTable";
import RazaoModals from "./components/RazaoModals";

import "./style.css";
import { useDraggableModal } from "../Balancete/utils/modalDraggable";

dayjs.extend(utc);

export default function LivroRazaoModal({
    visible,
    onClose,
    competencia,
    periodo,
    contaId,
    onSaved,
    title,
    zIndex,
    zIndices,
    onFocus,
    focusModal,
}) {
    // --- States ---
    const [editVisible, setEditVisible] = useState(false);
    const [editLoteVisible, setEditLoteVisible] = useState(false);
    const [dates, setDates] = useState([dayjs().startOf('month'), dayjs().endOf('month')]);
    const dataInicio = dates[0];
    const dataTermino = dates[1];
    const [contaContabilId, setContaContabilId] = useState("");
    const [nomeLancamento, setNomeLancamento] = useState("");
    const [lancamentos, setLancamentos] = useState([]);
    const [contasContabeis, setContasContabeis] = useState([]);
    const [editingId, setEditingId] = useState(null);
    const [loading, setLoading] = useState(false);
    const [selectedLancamentos, setSelectedLancamentos] = useState([]);
    const [descricaoFiltro, setDescricaoFiltro] = useState("");
    const [valorSearch, setValorSearch] = useState("");
    const [valorFiltro, setValorFiltro] = useState("");

    const draggableProps = useDraggableModal(zIndex, onFocus, {
        isVisible: visible,
        modalKey: "livroRazao",
        disabled: editVisible || editLoteVisible,
        initialWidth: 1300,
        initialHeight: 'auto'
    });

    // Ref para controle de persistência e mudanças externas
    const lastContextRef = useRef({ contaId: null, competencia: null, periodo: null });

    // --- Hooks ---
    const { showConfirm } = useLivroRazaoDownload({
        dataInicio: dates[0].startOf("month").hour(12).toISOString(),
        dataTermino: dates[1].endOf("month").hour(12).toISOString(),
        nomeLancamento: nomeLancamento || "",
        valor: (valorSearch || "").trim().replace(',', '.'),
        contaContabilId: contaContabilId || ""
    });

    // --- Handlers ---
    // --- Handlers ---
    const handlePesquisar = useCallback(async (forceId, forceDates) => {
        setLoading(true);
        try {
            const id = forceId !== undefined ? forceId : (contaContabilId || 0);

            // Prioriza as datas passadas como argumento (correção de latência)
            const [start, end] = forceDates || dates;

            const payload = {
                dataReferencia: start.startOf("month").hour(12).toISOString(),
                dataTermino: end.endOf("month").hour(12).toISOString(),
                nome: nomeLancamento || "",
                valor: (valorSearch || "").trim().replace(',', '.'),
                contaContabilDebito: { id: parseInt(id) }
            };

            const [resL, resS] = await Promise.all([
                Method_POST_CONTEXTO("/empresa/contabilidade/razao/listar", payload),
                Method_POST_CONTEXTO("/empresa/contabilidade/contas/saldos/pesquisar", {
                    dataInicio: payload.dataReferencia,
                    dataTermino: payload.dataTermino,
                })
            ]);

            const grouped = agruparLancamentosPorConta(resL, buildSaldoMap(resS));
            setLancamentos(grouped);
            setSelectedLancamentos([]);
        } catch (err) {
            console.error(err);
            message.error("Erro ao carregar dados do razão.");
        } finally {
            setLoading(false);
        }
    }, [dataInicio, dataTermino, nomeLancamento, valorSearch, contaContabilId]);

    // 1. Pesquisa Automática (Debounce) ao digitar Nome ou Valor no cabeçalho
    useEffect(() => {
        if (!visible) return;
        const timer = setTimeout(() => {
            handlePesquisar();
        }, 600);
        return () => clearTimeout(timer);
    }, [nomeLancamento, valorSearch, visible, handlePesquisar]);


    // 2. Pesquisa Imediata quando Datas ou Conta mudam
    useEffect(() => {
        if (!visible) return;
        handlePesquisar();
    }, [visible, contaContabilId, dates, handlePesquisar]);


    // --- Effects ---

    // 1. Inicialização de Dados Básicos (Plano de Contas)
    useEffect(() => {
        if (visible && contasContabeis.length === 0) {
            Method_POST_CONTEXTO("/empresa/contabilidade/conta-contabil/listar")
                .then(res => setContasContabeis(Array.isArray(res) ? res : (res?.data ?? [])))
                .catch(() => message.error("Erro ao carregar plano de contas"));
        }
    }, [visible, contasContabeis.length]);

    // 2. Sincronização com Props Externas (Trial Balance -> Ledger)
    useEffect(() => {
        if (!visible) return;

        const compVal = competencia ? dayjs(competencia).valueOf() : null;
        const lastCompVal = lastContextRef.current.competencia ? dayjs(lastContextRef.current.competencia).valueOf() : null;
        const periodStr = JSON.stringify(periodo);

        const hasChanged =
            lastContextRef.current.contaId !== contaId ||
            compVal !== lastCompVal ||
            lastContextRef.current.periodo !== periodStr;

        if (hasChanged) {
            // Atualiza ref
            lastContextRef.current = { contaId, competencia, periodo: periodStr };

            let newDates = [...dates];

            // Inicializa datas
            if (competencia) {
                const compDate = dayjs(competencia);
                if (compDate.isValid()) {
                    newDates = [compDate.startOf("month"), compDate.endOf("month")];
                }
            } else if (periodo && Array.isArray(periodo) && periodo.length === 2) {
                newDates = [dayjs(periodo[0]), dayjs(periodo[1])];
            }

            setDates(newDates);

            // Inicializa conta e pesquisa imediatamente com os novos valores
            if (contaId) setContaContabilId(contaId);
            handlePesquisar(contaId || 0, newDates);
        }
    }, [visible, contaId, competencia, periodo, handlePesquisar]);


    const handleDeletarLote = async () => {
        try {
            setLoading(true);
            await Promise.all(selectedLancamentos.map(l =>
                Method_POST_CONTEXTO("/empresa/contabilidade/lancamento/remover", { id: l.id })
            ));
            message.success(`${selectedLancamentos.length} lançamentos excluídos!`);
            setSelectedLancamentos([]);
            handlePesquisar();
            onSaved?.();
        } catch {
            message.error("Erro ao excluir lote.");
        } finally {
            setLoading(false);
        }
    };

    const handleDeletarUnico = async (id) => {
        try {
            await Method_POST_CONTEXTO("/empresa/contabilidade/lancamento/remover", { id });
            message.success("Lançamento removido");
            handlePesquisar();
            onSaved?.();
        } catch { message.error("Erro ao excluir"); }
    };

    // --- Memoized Data ---
    const lancamentosFiltrados = useMemo(() => {
        if (!descricaoFiltro && !valorFiltro) return lancamentos;

        const lowTextFilter = descricaoFiltro.toLowerCase().trim();
        const lowValFilter = valorFiltro.toLowerCase().trim().replace(',', '.');

        return lancamentos.map(c => {
            // Se o filtro bater na conta (código ou nome)
            const matchesAccount = (c.nome?.toLowerCase() || "").includes(lowTextFilter) ||
                (c.codigo?.toLowerCase() || "").includes(lowTextFilter);

            const filteredInner = Array.isArray(c.lancamentos) ? c.lancamentos.filter(l => {
                // Filtro de Texto (Histórico / Contrapartida)
                const matchesNome = (l.nome?.toLowerCase() || "").includes(lowTextFilter);
                const matchesContra = (l.contrapartida?.toLowerCase() || "").includes(lowTextFilter);
                const textMatches = matchesNome || matchesContra;

                // Filtro de Valor (Prioriza 'Começa com' e aceita variações de pontuação)
                const valDebStr = l.valorDebito?.toString() || "";
                const valCreStr = l.valorCredito?.toString() || "";
                const valDebFmt = l.valorDebito?.toLocaleString("pt-BR", { minimumFractionDigits: 2 }).replace(/\./g, ''); // remove separador de milhar para busca fluida
                const valCreFmt = l.valorCredito?.toLocaleString("pt-BR", { minimumFractionDigits: 2 }).replace(/\./g, '');

                const valueMatches = !lowValFilter || (
                    valDebStr.startsWith(lowValFilter) ||
                    valCreStr.startsWith(lowValFilter) ||
                    (valDebFmt && valDebFmt.includes(valorFiltro.trim())) ||
                    (valCreFmt && valCreFmt.includes(valorFiltro.trim()))
                );

                return (lowTextFilter ? textMatches : true) && (valorFiltro ? valueMatches : true);
            }) : [];

            return {
                ...c,
                lancamentos: (matchesAccount && !valorFiltro) ? c.lancamentos : filteredInner
            };
        }).filter(c => c.lancamentos.length > 0);
    }, [lancamentos, descricaoFiltro, valorFiltro]);

    return (
        <>
            <Modal
                title={title || "Livro Razão"}
            open={visible}
            onCancel={onClose}
            width={1300}
            footer={null}
            getContainer={() => document.body}
            className="razao-modal-premium"
            {...draggableProps}
            zIndex={zIndex || 10001}
            styles={{
                ...draggableProps.styles,
                body: {
                    maxHeight: "75vh",
                    minHeight: '400px',
                    padding: '0 24px 24px 24px',
                    display: 'flex',
                    flexDirection: 'column',
                    overflowY: "auto"
                }
            }}
        >
            <div style={{
                display: 'flex',
                flexDirection: 'column',
                height: '100%',
                gap: '16px'
            }}>
                <RazaoHeader
                    dates={dates}
                    setDates={setDates}
                    contaContabilId={contaContabilId}
                    setContaContabilId={setContaContabilId}
                    contasContabeis={contasContabeis}
                    nomeLancamento={nomeLancamento}
                    setNomeLancamento={setNomeLancamento}
                    valorSearch={valorSearch}
                    setValorSearch={setValorSearch}
                    handlePesquisar={handlePesquisar}
                    showConfirm={showConfirm}
                    exportLivroRazaoExcel={exportLivroRazaoExcel}
                    lancamentos={lancamentos}
                />

                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexShrink: 0 }}>
                    <Space size="middle">
                        <Button
                            type="primary"
                            icon={<PlusOutlined />}
                            size="large"
                            onClick={() => {
                                setEditingId(null);
                                setEditVisible(true);
                                focusModal?.('editLancamentoRazao');
                            }}
                            className="btn-gradient"
                        >
                            Incluir Lançamento
                        </Button>

                        <Button
                            icon={<FileExcelOutlined />}
                            size="large"
                            onClick={() => {
                                const allLancs = lancamentos.flatMap(c => c.lancamentos || []);
                                exportLancamentosExcel(allLancs);
                            }}
                            style={{ borderRadius: '10px' }}
                        >
                            Exportar Lançamentos
                        </Button>

                        {selectedLancamentos.length > 0 && (
                            <Space>
                                <Button
                                    icon={<EditOutlined />}
                                    size="large"
                                    onClick={() => {
                                        setEditLoteVisible(true);
                                        focusModal?.('editRazaoLote');
                                    }}
                                    style={{ borderRadius: '10px', fontWeight: 600, color: '#1890ff' }}
                                >
                                    Editar Lote ({selectedLancamentos.length})
                                </Button>
                                <Popconfirm
                                    title={`Excluir ${selectedLancamentos.length} lançamentos?`}
                                    onConfirm={handleDeletarLote}
                                    okText="Sim, excluir"
                                    cancelText="Não"
                                >
                                    <Button
                                        danger
                                        size="large"
                                        icon={<DeleteOutlined />}
                                        style={{ borderRadius: '10px', fontWeight: 600 }}
                                    >
                                        Excluir Lote
                                    </Button>
                                </Popconfirm>
                            </Space>
                        )}
                    </Space>

                    <Space size="middle">
                        <Input
                            placeholder="Filtrar histórico..."
                            size="large"
                            allowClear
                            value={descricaoFiltro}
                            onChange={e => setDescricaoFiltro(e.target.value)}
                            style={{ width: 250 }}
                            prefix={<SearchOutlined style={{ color: '#1890ff' }} />}
                            className="razao-search-input"
                        />
                        <Input
                            placeholder="Valor..."
                            size="large"
                            allowClear
                            value={valorFiltro}
                            onChange={e => setValorFiltro(e.target.value)}
                            style={{ width: 140 }}
                            prefix={<span style={{ color: '#1890ff', fontWeight: 'bold' }}>R$</span>}
                            className="razao-search-input"
                        />
                    </Space>
                </div>

                {/* Container da Tabela com Flex 1 para crescer e ocupar o resto da janela */}
                <div style={{ flex: 1, overflowY: 'auto', minHeight: 0 }}>
                    <RazaoTable
                        loading={loading}
                        lancamentosFiltrados={lancamentosFiltrados}
                        selectedLancamentos={selectedLancamentos}
                        setSelectedLancamentos={setSelectedLancamentos}
                        setEditingId={setEditingId}
                        setEditVisible={(val) => {
                            setEditVisible(val);
                            if (val) focusModal?.('editLancamentoRazao');
                        }}
                        handleDeletar={handleDeletarUnico}
                    />
                </div>

            </div>

        </Modal>

        {typeof document !== 'undefined' && createPortal(
            <RazaoModals
                editVisible={editVisible}
                setEditVisible={(val) => {
                    setEditVisible(val);
                    if (val) focusModal?.('editLancamentoRazao');
                }}
                editingId={editingId}
                dataInicio={dataInicio}
                contasContabeis={contasContabeis}
                handlePesquisar={handlePesquisar}
                onSaved={onSaved}
                editLoteVisible={editLoteVisible}
                setEditLoteVisible={setEditLoteVisible}
                selectedLancamentos={selectedLancamentos}
                setSelectedLancamentos={setSelectedLancamentos}
                zIndex={(zIndex || 10000) + 1000}
                onFocus={() => focusModal?.('editLancamentoRazao')}
                editLoteZIndex={(zIndex || 10000) + 1000}
                onFocusLote={() => focusModal?.('editRazaoLote')}
            />,
            document.body
        )}
        </>
    );
}